import { addNewPatientDevice } from "app/services/transmission/patient";
import { useGetDeviceListQuery } from "app/store/devices/deviceApi";
import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  CLINIC_NAMES,
  manufacturerMappings,
  TOAST_SEVERITY,
} from "static/constants";
import { showToast } from "utils/toast";
import { useGetManufacturersQuery } from "app/store/manufacturers/api/manufacturersApi";
import { setPatientManufacturers } from "app/store/manualUpload/slice/manualUploadSlice";
import useAuthorization from "hooks/useAuthorization";

const useAddNewDeviceForm = ({
  patientId,
  handleAddNewDevice,
  isManualUpload = false,
}) => {
  const deviceFieldsModel = {
    doi: null,
    account: "",
    modelName: "",
    modelNumber: "",
    manufacturer: "",
  };

  const { accounts } = useSelector((state) => state.account);
  const { patientManufacturers } = useSelector((state) => state.manualUpload);

  const { accessedAccountMappings } = useAuthorization();

  const { data: manufacturers } = useGetManufacturersQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const { data: deviceList } = useGetDeviceListQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const [manufacturersList, setManufacturersList] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deviceFields, setDeviceFields] = useState(deviceFieldsModel);

  const dispatch = useDispatch();

  const loadManufacturerList = useCallback(async () => {
    const updatedManufacturerList = manufacturers?.map(({ name }) => ({
      value: name,
      label: name,
    }));

    setManufacturersList(updatedManufacturerList);
  }, [manufacturers]);

  useEffect(() => {
    loadManufacturerList();

    return () => loadManufacturerList();
  }, [loadManufacturerList]);

  const accountList = accounts
    ?.filter(({ name }) => name !== CLINIC_NAMES.UCH)
    .map(({ name }) => {
      return { value: name, label: name };
    });

  const onUpdate = (field, value) => {
    setIsSubmitClicked(false);

    const fieldsCopy = { ...deviceFields };
    fieldsCopy[field] = value;

    /**
     * 'Manual Upload' requires the values in the keys
     */
    if (isManualUpload) {
      if (field === "account") {
        const match = accessedAccountMappings?.find(
          (account) => account.name === value
        );
        fieldsCopy.accountFolder = match.folder || "";
        fieldsCopy.physicians = [];
      }

      if (field === "manufacturer") {
        const matchedManufacturer = manufacturerMappings.find(
          (manufacturer) => manufacturer.name === value
        );

        fieldsCopy.rpmDevices = {};
        fieldsCopy.isRpmDevice = false;
        fieldsCopy.additionalManufacturer = [];
        fieldsCopy.manufacturerFolder = matchedManufacturer?.folder || "";
      }
    }

    const updatedDeviceFields = { ...deviceFields, ...fieldsCopy };
    setDeviceFields(updatedDeviceFields);
  };

  const checkFormValid = () => {
    const { account, doi, manufacturer, modelName, modelNumber } = deviceFields;
    return !!(account && doi && manufacturer && modelName && modelNumber);
  };

  const onFormSubmit = async () => {
    setIsSubmitClicked(true);
    if (!checkFormValid()) {
      setIsSubmitting(false);
      return;
    }
    setShowConfirmModal(true);
  };

  const handleFormSubmitConfirm = async () => {
    try {
      setIsSubmitting(true);

      let patientManufacturerOptions = [...patientManufacturers];
      const {
        rpmDevices,
        physicians,
        isRpmDevice,
        manufacturer,
        accountFolder,
        manufacturerFolder,
        additionalManufacturer,
      } = deviceFields;

      // update the manufacturers list for this patient to show in the manual upload form
      if (isManualUpload) {
        patientManufacturerOptions.push({
          name: manufacturer,
          rpmDevice: false,
        });
        dispatch(setPatientManufacturers(patientManufacturerOptions));
      }

      const response = await addNewPatientDevice(patientId, deviceFields);

      if (response?.type === "Success") {
        showToast({ message: response?.message });

        handleAddNewDevice({
          ...response?.data,
          rpmDevices,
          physicians,
          isRpmDevice,
          accountFolder,
          manufacturerFolder,
          additionalManufacturer,
        });
      }
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    } finally {
      setIsSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  return {
    deviceList,
    accountList,
    deviceFields,
    isSubmitting,
    isSubmitClicked,
    showConfirmModal,
    manufacturersList,

    onUpdate,
    onFormSubmit,
    setShowConfirmModal,
    handleFormSubmitConfirm,
  };
};

export default useAddNewDeviceForm;
